import groupBy from "lodash/groupBy";
import type {
    CarDataType,
    CompareMotorizationType,
    EquipmentEcoSpecNameIdType,
    SpecItemType,
} from "../../../../common-deprecated/features/compare/utils/newCarCompareTypes";
import type { EcoSpecType } from "../compareTypes";
import type { CompareV2LabelType } from "../labels";
import {
    EmissionSpecCode,
    EvSpecCode,
    FuelSpecCode,
    HydrogenSpecCode,
} from "../../../shared-logic/utils/constants/SpecConstants";
import { EFFICIENCY_CLASS, FlexMatrixPriceType } from "../../../../common-deprecated/constants";
import type { AemFmPriceType } from "../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { valuesToRange } from "../../../../common-deprecated/utils/specs";

export type CarPriceAndIdType = {
    currency: string;
    price: number;
    discount: number;
    carId: string;
    promotions?: string[];
};
/**
 * @param priceType - Add priceType to support exclVat prices
 */
export const getSingleCarCashPriceAndId = (
    car: CarDataType,
    engine: CompareMotorizationType,
    priceType?: FlexMatrixPriceType | AemFmPriceType,
): CarPriceAndIdType => {
    if (!car.prices || !engine) {
        return { currency: "", price: 0, discount: 0, carId: "", promotions: [] };
    }
    const correctEngine = car.prices.find((eng) => eng.id === engine.engineId);
    const correctTransmission =
        correctEngine && correctEngine.transmissions.find((transmission) => transmission.id === engine.transmissionId);
    const correctWheeldrive =
        correctTransmission &&
        correctTransmission.wheeldrives.find((wheeldrive) => wheeldrive.id === engine.wheeldriveId);

    // Get the extra price of the colour if there is one
    const colourPrice = car.availableColours.find((colour) => colour.code === car.colourCode)?.price;

    return correctWheeldrive
        ? {
              currency: correctWheeldrive.from.currency,
              price:
                  priceType === FlexMatrixPriceType.ExclVat
                      ? correctWheeldrive.from.net + (colourPrice?.net || 0)
                      : correctWheeldrive.from.list + (colourPrice?.list || 0),
              discount:
                  priceType === FlexMatrixPriceType.ExclVat
                      ? correctWheeldrive.from.netWithDiscount + (colourPrice?.netWithDiscount || 0)
                      : correctWheeldrive.from.listWithDiscount + (colourPrice?.listWithDiscount || 0),

              carId: correctWheeldrive.from.car.CarID,
              promotions: correctWheeldrive.from.car.Promotions,
          }
        : { currency: "", price: 0, discount: 0, carId: "", promotions: [] };
};

export const getCarPriceAndCarIdForEngine = (
    cars: CarDataType[],
    engines: CompareMotorizationType[],
): CarPriceAndIdType[] => {
    return cars.map((car, index) => getSingleCarCashPriceAndId(car, engines[index]));
};

export const getCarModelColourUpholsteryIds = (
    car: CarDataType,
    selectedEngine: CompareMotorizationType,
): { modelId: string; carId: string; colourId: string; upholsteryId: string } => {
    if (!selectedEngine) {
        return { modelId: "", carId: "", colourId: "", upholsteryId: "" };
    }
    const correctEngine = car.prices.find((engine) => engine.id === selectedEngine.engineId);
    const correctTransmission =
        correctEngine &&
        correctEngine.transmissions.find((transmission) => transmission.id === selectedEngine.transmissionId);
    const correctWheeldrive =
        correctTransmission &&
        correctTransmission.wheeldrives.find((wheeldrive) => wheeldrive.id === selectedEngine.wheeldriveId);

    return correctWheeldrive
        ? {
              modelId: correctWheeldrive.from.car.ModelID,
              carId: correctWheeldrive.from.car.CarID,
              colourId: correctWheeldrive.from.car.ExteriorColourID,
              upholsteryId: correctWheeldrive.from.car.UpholsteryID,
          }
        : { modelId: "", carId: "", colourId: "", upholsteryId: "" };
};

const mapEcoSpec = (spec: EquipmentEcoSpecNameIdType): EcoSpecType => {
    return {
        id: `${spec.id}`,
        titles: [spec.name],
        unit: spec.unit || "",
        values: spec.values ? spec.values.map((values) => `${values.value}`) : [],
    };
};

const transposeArray = (inputArray: EquipmentEcoSpecNameIdType[]): { titles: string[]; values: string[] } => {
    const transposed: { titles: string[]; values: string[] } = {
        titles: [],
        values: [],
    };

    inputArray.forEach((obj) => {
        if (obj.values) {
            obj.values.forEach((item, index) => {
                if (!transposed.values[index] || transposed.values[index] === "") {
                    transposed.titles[index] = obj.name;
                    transposed.values[index] = `${item.value}`;
                }
            });
        }
    });

    return transposed;
};

const convertSpecsToRanges = (specs: EquipmentEcoSpecNameIdType[]): EquipmentEcoSpecNameIdType[] => {
    const groupedSpecs = groupBy(specs, "short");
    return Object.values(groupedSpecs)
        .map((groupedSpecArray) => {
            const valuesByIndex: number[][] = [];
            const unitByIndex: string[] = [];
            groupedSpecArray.forEach((spec) => {
                spec.values?.forEach(({ value }, index) => {
                    if (valuesByIndex[index]) {
                        valuesByIndex[index].push(parseFloat(`${value || ""}`));
                    } else {
                        valuesByIndex[index] = [parseFloat(`${value || ""}`)];
                    }
                    unitByIndex[index] = typeof spec.unit === "string" && value ? spec.unit : "";
                });
            });
            const spec: EquipmentEcoSpecNameIdType = {
                ...groupedSpecArray[0],
                name: (groupedSpecArray.length > 1 ? groupedSpecArray[0].short : groupedSpecArray[0].name) || "",
                unit: unitByIndex || "",
                values:
                    groupedSpecArray.length > 1
                        ? valuesByIndex.map((values) => {
                              return {
                                  value: valuesToRange(values),
                              };
                          })
                        : groupedSpecArray[0].values || [],
            };
            return spec;
        })
        .filter(Boolean);
};

/**
 * Goes through multiple specs within a constraint and fills out similar data
 * @param specs All the specs that contain similar/identical data
 * @returns EcoSpec or Null depending on if primarySpec doesn't exist
 */
const mapMultiEcoSpec = (specs: EquipmentEcoSpecNameIdType[]): EcoSpecType | null => {
    if (!specs.length) return null;
    const primarySpec = specs[0];
    const primarySpecHasEmptyValues = !!primarySpec.values?.find((val) => val.value === "");
    if (!primarySpecHasEmptyValues) {
        return mapEcoSpec(primarySpec);
    } else {
        return {
            id: `${primarySpec.id}`,
            unit: primarySpec.unit || "",
            ...transposeArray(specs),
        };
    }
};

const getSpecRange = (
    allSpecs: EquipmentEcoSpecNameIdType[],
    minKey: string,
    maxKey: string,
    tooltip?: string,
): EcoSpecType | null => {
    const foundSpecMin = allSpecs.find(
        (spec) => spec.code.toLowerCase().includes(minKey) && !spec.code.toLowerCase().includes(maxKey),
    );
    const foundSpecMax = allSpecs.find((spec) => spec.code.toLowerCase().includes(maxKey));
    if (foundSpecMin && foundSpecMin.values) {
        return {
            id: `${foundSpecMin.id}`,
            titles: [foundSpecMin.short || foundSpecMin.name],
            unit: foundSpecMin.unit || "",
            tooltip,
            values: foundSpecMin.values.map((minVals, index) => {
                const maxValue = foundSpecMax?.values?.[index].value || "";
                return maxValue === minVals.value ? `${maxValue}` : valuesToRange([maxValue, minVals.value]);
            }),
        };
    }
    return null;
};

export const getEcoSpecs = (specs: SpecItemType[], labels: Record<CompareV2LabelType, string>): EcoSpecType[] => {
    const allSpecs = specs.flatMap((cat) => cat.specs);
    const specList: EcoSpecType[] = [];
    // Fuel consumption spec -- already formatted if exists
    const foundFuelSpec = mapMultiEcoSpec(
        convertSpecsToRanges(
            allSpecs.filter((spec) => {
                const lowerCode = spec.code.toLowerCase();
                return (
                    ["fcc", "com"].includes(lowerCode) ||
                    lowerCode.includes(FuelSpecCode.FuelCombined) ||
                    lowerCode.includes(FuelSpecCode.FuelCombinedUfWeighted) ||
                    lowerCode.includes(HydrogenSpecCode.FuelCombinedKg) ||
                    lowerCode.includes(FuelSpecCode.FuelCombinedNormalWltp)
                );
            }),
        ),
    );
    if (foundFuelSpec) specList.push(foundFuelSpec);

    const powerConsumptionAlt = mapMultiEcoSpec(
        convertSpecsToRanges(
            allSpecs.filter(
                (spec) =>
                    spec.code.toLowerCase().includes(EvSpecCode.PowerConsumptionWeighted) ||
                    spec.code.toLowerCase().includes(EvSpecCode.PowerConsumptionWeightedMax),
            ),
        ),
    );
    if (powerConsumptionAlt) specList.push(powerConsumptionAlt);

    // Power consumption spec
    const foundPowerSpec = mapMultiEcoSpec(
        convertSpecsToRanges(allSpecs.filter((spec) => spec.code.toLowerCase().includes(EvSpecCode.PowerConsumption))),
    );
    if (foundPowerSpec) specList.push(foundPowerSpec);

    // CO2 Combined
    const foundCo2Spec = mapMultiEcoSpec(
        convertSpecsToRanges(
            allSpecs
                .filter((spec) => spec.code.toLowerCase().includes("co2"))
                .sort((a, b) => {
                    // Check for presence of "co2combinedwltp"
                    const hasCombinedWltpA = a.code.toLowerCase().includes(EmissionSpecCode.Co2Combined);
                    const hasCombinedWltpB = b.code.toLowerCase().includes(EmissionSpecCode.Co2Combined);

                    // Sort specs with "co2combinedwltp" first (descending order)
                    if (hasCombinedWltpA && !hasCombinedWltpB) return -1;
                    if (!hasCombinedWltpA && hasCombinedWltpB) return 1;

                    // If both have (or neither have) "co2combinedwltp", sort by original order
                    return 0;
                }),
        ),
    );
    if (foundCo2Spec) specList.push(foundCo2Spec);

    // EFF-CLASS
    const foundEnergyClassSpec = allSpecs.find((spec) =>
        spec.code.toLowerCase().includes(EFFICIENCY_CLASS.toLowerCase()),
    );
    if (foundEnergyClassSpec) specList.push(mapEcoSpec(foundEnergyClassSpec));

    // EV - Range
    const foundRangeSpec = getSpecRange(allSpecs, EvSpecCode.Range, EvSpecCode.RangeMax, labels.disclaimerEv);

    const foundElectricCombinedRangeSpec = getSpecRange(
        allSpecs,
        EvSpecCode.ElectricCombinedRange,
        EvSpecCode.ElectricCombinedRangeMax,
    );
    if (foundRangeSpec && foundElectricCombinedRangeSpec) {
        specList.push({
            ...foundRangeSpec,
            values: foundRangeSpec.values.map(
                (val, index) => val || foundElectricCombinedRangeSpec.values[index] || "",
            ),
        });
    } else {
        if (!foundRangeSpec && foundElectricCombinedRangeSpec) {
            specList.push(foundElectricCombinedRangeSpec);
        }
        if (foundRangeSpec && !foundElectricCombinedRangeSpec) {
            specList.push(foundRangeSpec);
        }
    }
    const foundElectricCityRangeSpec = getSpecRange(
        allSpecs,
        EvSpecCode.ElectricCityRange,
        EvSpecCode.ElectricCityRangeMax,
    );
    if (foundElectricCityRangeSpec) {
        specList.push(foundElectricCityRangeSpec);
    }

    // Disabled Battery Energy spec for now as it is not clear which code should be used.
    // EV - Battery energy
    // const foundBatteryEnergy = allSpecs.find((spec) => spec.code.toLowerCase().includes(EvSpecCode.BatteryEnergy));
    // if (foundBatteryEnergy) specList.push(mapEcoSpec(foundBatteryEnergy));

    // EV - Charging time
    const foundChargingTime = allSpecs.find((spec) => spec.code.toLowerCase().includes(EvSpecCode.ChargingTime));
    if (foundChargingTime) specList.push(mapEcoSpec(foundChargingTime));

    return specList;
};
